import { FC } from "react";

import { Stack } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { FormType, hasPermissionOnUserManagement } from "@simplyk/common";
import { UseFormReturn } from "react-hook-form";

import type { StepAdvancedSettingsFormData } from "../types";

import { NotificationEmailsInput } from "./NotificationEmailsInput";

import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { useTranslate } from "@/common/hooks/useTranslate";
import { LiveEditorBlock } from "@/components/LiveEditorElements/LiveEditorBlock/LiveEditorBlock";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";

const sectionSx = (theme: Theme) => ({
  backgroundColor: theme.palette.surface.neutral.supershy,
  borderRadius: 1,
  p: 3,
});

const sectionOptionsSx = (theme: Theme) => ({
  "& > *": {
    border: `1px solid ${theme.palette.border.neutral.quiet}`,
  },
});

interface NotificationEmailsBlockProps {
  form: UseFormReturn<StepAdvancedSettingsFormData>;
  formType: FormType;
}

export const NotificationEmailsBlock: FC<NotificationEmailsBlockProps> = ({ form, formType }) => {
  const { t } = useTranslate();
  const { currentUser } = useCurrentUserContext();

  if (hasPermissionOnUserManagement(currentUser?.newPermissions)) {
    return null;
  }

  const labelKey =
    formType === FormType.DonationForm ? "donationForm.notificationEmails" : "ticketing.notificationEmails";

  return (
    <Stack gap={2} sx={sectionSx} data-test="advanced-settings-notification-emails-block">
      <DashboardTypography variant="subtitle1">{t("dashboard", "notifications")}</DashboardTypography>

      <Stack gap={2} sx={sectionOptionsSx}>
        <LiveEditorBlock>
          <NotificationEmailsInput control={form.control} label={t("dashboard", labelKey)} />
        </LiveEditorBlock>
      </Stack>
    </Stack>
  );
};
